import {AwCheckbox} from '@ascension-tracker/ascension-ui';
import {TagChips} from './TagChips.js';
import type {ReleaseSummary, Task, TaskEnvironment, TaskPriority} from '../hooks/useTasks.js';

function formatDue(value: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

const environments: TaskEnvironment[] = ['development', 'testing', 'integration', 'early_access', 'production'];
const priorities: TaskPriority[] = ['critical', 'high', 'medium', 'low'];

export function TaskRow({task, onToggle, onOpen, draggable, onDragStart, onDragEnd, indented, hasChildren, collapsed, onToggleCollapse, releases, onUpdateFields}: {
  task: Task;
  onToggle: () => void;
  onOpen: () => void;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  indented?: boolean;
  hasChildren?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  releases?: ReleaseSummary[];
  onUpdateFields?: (fields: {priority?: TaskPriority; environment?: TaskEnvironment | null; releaseId?: string | null}) => void;
}) {
  const due = formatDue(task.dueOn);
  return (
    <div className={`task-row${task.completedAt ? ' is-complete' : ''}${indented ? ' task-row--indented' : ''}`} draggable={draggable} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {indented && <span className="task-row__branch" aria-hidden="true"/>}
      {hasChildren ? (
        <button type="button" className="task-row__collapse" aria-label={collapsed ? 'Expand subtasks' : 'Collapse subtasks'} aria-expanded={!collapsed} onClick={onToggleCollapse}>{collapsed ? '›' : '⌄'}</button>
      ) : indented ? <span className="task-row__collapse-spacer" aria-hidden="true"/> : null}
      <AwCheckbox id={`task-complete-${task.id}`} label="" checked={Boolean(task.completedAt)} onChange={onToggle}/>
      <button type="button" className="task-row__title" onClick={onOpen}>{task.title}</button>
      <TagChips tags={task.tags}/>
      {onUpdateFields && (
        <div className="task-row__fields">
          <select aria-label="Priority" className={`task-row__priority-select task-row__priority-select--${task.priority}`} value={task.priority} onChange={event => onUpdateFields({priority: event.target.value as TaskPriority})}>
            {priorities.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
          <select aria-label="Environment" className={`task-row__environment-select${task.environment ? ` task-row__environment-select--${task.environment}` : ''}`} value={task.environment ?? ''} onChange={event => onUpdateFields({environment: (event.target.value || null) as TaskEnvironment | null})}>
            <option value="">No environment</option>
            {environments.map(value => <option key={value} value={value}>{value.replace('_', ' ')}</option>)}
          </select>
          <select aria-label="Release" className={`task-row__release-select${task.releaseId ? ' task-row__release-select--set' : ''}`} value={task.releaseId ?? ''} onChange={event => onUpdateFields({releaseId: event.target.value || null})}>
            <option value="">No release</option>
            {releases?.map(release => <option key={release.id} value={release.id}>{release.version}</option>)}
          </select>
        </div>
      )}
      {task.assigneeName && <span className="task-row__assignee">{task.assigneeName}</span>}
      {due && <span className="task-row__due">{due}</span>}
    </div>
  );
}
